import React, { useEffect, useState, useContext } from 'react';
import { Table, Tag, Button, Modal, Input, Upload, Space, message, Spin } from 'antd';
import { DownloadOutlined, CheckOutlined, CloseOutlined, UploadOutlined, EyeOutlined } from '@ant-design/icons';
import axios from 'axios';
import { BACKEND_URL } from '../../../config/config';
import { NotificationContext } from '../../context/NotificationWrapper';

const { TextArea } = Input;

// Labels pour les types de transition (utilisés dans le tableau ET la modal de détails)
const TRANSITION_TYPE_LABELS = {
  creation: 'Création',
  to_zero: 'Modification',
  zero_to_zero: 'Modification',
  from_zero: 'Modification',
};

// Labels pour les types de période
const PERIOD_TYPE_LABELS = {
  monthly: 'Mensuel',
  semi_annual_s1: 'Semestriel S1',
  semi_annual_s2: 'Semestriel S2',
  annual: 'Annuel',
};

const AttestationRequestsList = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const { openNotification } = useContext(NotificationContext);
  const backendUrl = BACKEND_URL;

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = () => {
    setLoading(true);
    axios
      .get(`${backendUrl}/api/attestation-request/list`, {
        withCredentials: true,
      })
      .then((response) => {
        setRequests(response.data.data);
      })
      .catch((error) => {
        console.error('Erreur lors du chargement des demandes', error);
        openNotification({
          type: 'error',
          message: 'Erreur',
          description: 'Impossible de charger les demandes d\'attestation.',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDownloadDsn = (requestId) => {
    axios
      .get(`${backendUrl}/api/attestation-request/${requestId}/download-dsn`, {
        withCredentials: true,
        responseType: 'blob',
      })
      .then((response) => {
        const contentDisposition = response.headers['content-disposition'];
        let filename = 'dsn.pdf';
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error('Erreur lors du téléchargement', error);
        openNotification({
          type: 'error',
          message: 'Erreur',
          description: 'Impossible de télécharger le fichier DSN.',
        });
      });
  };

  const handleApprove = (requestId) => {
    Modal.confirm({
      title: 'Approuver la demande',
      content: 'Êtes-vous sûr de vouloir approuver cette demande d\'attestation ?',
      okText: 'Approuver',
      cancelText: 'Annuler',
      onOk: () => {
        setLoading(true);
        axios
          .post(`${backendUrl}/api/attestation-request/${requestId}/approve`, {}, {
            withCredentials: true,
          })
          .then(() => {
            openNotification({
              type: 'success',
              message: 'Succès',
              description: 'La demande a été approuvée avec succès.',
            });
            fetchRequests();
          })
          .catch((error) => {
            console.error('Erreur lors de l\'approbation', error);
            openNotification({
              type: 'error',
              message: 'Erreur',
              description: error.response?.data?.message || 'Impossible d\'approuver la demande.',
            });
          })
          .finally(() => {
            setLoading(false);
          });
      },
    });
  };

  const showRejectModal = (request) => {
    setSelectedRequest(request);
    setRejectionReason('');
    setRejectModalVisible(true);
  };

  const handleReject = () => {
    if (!rejectionReason.trim()) {
      message.error('Veuillez fournir une raison pour le rejet.');
      return;
    }

    setLoading(true);
    axios
      .post(`${backendUrl}/api/attestation-request/${selectedRequest.id}/reject`, {
        reason: rejectionReason,
      }, {
        withCredentials: true,
      })
      .then(() => {
        openNotification({
          type: 'success',
          message: 'Succès',
          description: 'La demande a été rejetée.',
        });
        setRejectModalVisible(false);
        fetchRequests();
      })
      .catch((error) => {
        console.error('Erreur lors du rejet', error);
        openNotification({
          type: 'error',
          message: 'Erreur',
          description: error.response?.data?.message || 'Impossible de rejeter la demande.',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const showDetailsModal = (requestId) => {
    setLoading(true);
    axios
      .get(`${backendUrl}/api/attestation-request/${requestId}`, {
        withCredentials: true,
      })
      .then((response) => {
        setSelectedRequest(response.data.data);
        setDetailsModalVisible(true);
      })
      .catch((error) => {
        console.error('Erreur lors du chargement des détails', error);
        openNotification({
          type: 'error',
          message: 'Erreur',
          description: 'Impossible de charger les détails de la demande.',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleReplaceDsn = (requestId, file) => {
    const formData = new FormData();
    formData.append('dsnFile', file);

    setLoading(true);
    axios
      .post(`${backendUrl}/api/attestation-request/${requestId}/replace-dsn`, formData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(() => {
        openNotification({
          type: 'success',
          message: 'Succès',
          description: 'Le fichier DSN a été remplacé avec succès.',
        });
        fetchRequests();
      })
      .catch((error) => {
        console.error('Erreur lors du remplacement', error);
        openNotification({
          type: 'error',
          message: 'Erreur',
          description: error.response?.data?.message || 'Impossible de remplacer le fichier DSN.',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
    },
    {
      title: 'Entreprise',
      key: 'company',
      render: (_, record) => (
        <div>
          <div><strong>{record.company?.name}</strong></div>
          <div style={{ fontSize: '12px', color: '#888' }}>
            Code: {record.company?.membershipCode}
          </div>
        </div>
      ),
    },
    {
      title: 'Période',
      key: 'period',
      render: (_, record) => {
        const date = record.declaration?.inputedDate || record.declaration?.inputedDate;
        if (!date) return '-';
        const dateObj = new Date(date * 1000);
        return dateObj.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
      },
    },
    {
      title: 'Type période',
      dataIndex: 'periodType',
      key: 'periodType',
      render: (periodType) => {
        const colors = {
          monthly: 'blue',
          semi_annual_s1: 'purple',
          semi_annual_s2: 'purple',
          annual: 'cyan',
        };
        return <Tag color={colors[periodType] || 'default'}>{PERIOD_TYPE_LABELS[periodType] || periodType}</Tag>;
      },
      filters: [
        { text: 'Mensuel', value: 'monthly' },
        { text: 'Semestriel S1', value: 'semi_annual_s1' },
        { text: 'Semestriel S2', value: 'semi_annual_s2' },
        { text: 'Annuel', value: 'annual' },
      ],
      onFilter: (value, record) => record.periodType === value,
    },
    {
      title: 'Type',
      dataIndex: 'transitionType',
      key: 'transitionType',
      render: (type) => {
        return <Tag color="blue">{TRANSITION_TYPE_LABELS[type] || type}</Tag>;
      },
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colors = {
          pending: 'orange',
          approved: 'green',
          rejected: 'red',
        };
        const labels = {
          pending: 'En attente',
          approved: 'Approuvée',
          rejected: 'Rejetée',
        };
        return <Tag color={colors[status]}>{labels[status]}</Tag>;
      },
    },
    {
      title: 'Date demande',
      dataIndex: 'requestedAt',
      key: 'requestedAt',
      render: (timestamp) => new Date(timestamp * 1000).toLocaleDateString('fr-FR'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => showDetailsModal(record.id)}
          >
            Détails
          </Button>
          {record.status === 'pending' && (
            <>
              <Button
                size="small"
                type="primary"
                icon={<CheckOutlined />}
                onClick={() => handleApprove(record.id)}
              >
                Approuver
              </Button>
              <Button
                size="small"
                danger
                icon={<CloseOutlined />}
                onClick={() => showRejectModal(record)}
              >
                Rejeter
              </Button>
            </>
          )}
          {record.dsnFile && (
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => handleDownloadDsn(record.id)}
            >
              DSN
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="block-right" style={{ padding: '24px' }}>
      <Spin spinning={loading}>
        <div style={{ marginBottom: '24px' }}>
          <h2>Demandes d'attestation</h2>
          <p style={{ color: '#666' }}>
            Gérer les demandes d'attestation en attente de validation
          </p>
        </div>

        <Table
          columns={columns}
          dataSource={requests}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} demandes`,
          }}
        />
      </Spin>

      {/* Modal de rejet */}
      <Modal
        title="Rejeter la demande"
        open={rejectModalVisible}
        onOk={handleReject}
        onCancel={() => setRejectModalVisible(false)}
        okText="Rejeter"
        cancelText="Annuler"
        okButtonProps={{ danger: true }}
      >
        <p>Veuillez indiquer la raison du rejet :</p>
        <TextArea
          rows={4}
          value={rejectionReason}
          onChange={(e) => setRejectionReason(e.target.value)}
          placeholder="Ex: Le fichier DSN fourni est invalide..."
        />
      </Modal>

      {/* Modal de détails */}
      <Modal
        title="Détails de la demande"
        open={detailsModalVisible}
        onCancel={() => setDetailsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailsModalVisible(false)}>
            Fermer
          </Button>,
        ]}
        width={700}
      >
        {selectedRequest && (
          <div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Entreprise:</strong> {selectedRequest.company?.name} ({selectedRequest.company?.membershipCode})
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Type:</strong> {TRANSITION_TYPE_LABELS[selectedRequest.transitionType] || selectedRequest.transitionType}
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Statut:</strong> {selectedRequest.status}
            </div>
            {selectedRequest.rejectionReason && (
              <div style={{ marginBottom: '16px' }}>
                <strong>Raison du rejet:</strong>
                <div style={{ padding: '8px', background: '#f5f5f5', borderRadius: '4px', marginTop: '8px' }}>
                  {selectedRequest.rejectionReason}
                </div>
              </div>
            )}
            {selectedRequest.dsnFile && (
              <div style={{ marginBottom: '16px' }}>
                <strong>Fichier DSN:</strong>
                <div style={{ marginTop: '8px' }}>
                  <div>{selectedRequest.dsnFile.originalName}</div>
                  <div style={{ fontSize: '12px', color: '#888' }}>
                    Taille: {(selectedRequest.dsnFile.fileSize / 1024).toFixed(2)} KB
                  </div>
                  <Space style={{ marginTop: '8px' }}>
                    <Button
                      icon={<DownloadOutlined />}
                      onClick={() => handleDownloadDsn(selectedRequest.id)}
                    >
                      Télécharger
                    </Button>
                    {selectedRequest.status === 'pending' && (
                      <Upload
                        accept=".pdf,.xml,.txt"
                        showUploadList={false}
                        beforeUpload={(file) => {
                          handleReplaceDsn(selectedRequest.id, file);
                          return false;
                        }}
                      >
                        <Button icon={<UploadOutlined />}>Remplacer</Button>
                      </Upload>
                    )}
                  </Space>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AttestationRequestsList;
